import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, FileText, Image, Video, Music, Mail, Share2 } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface PostType {
  id: number;
  name: string;
  type: string;
  category: string;
  status: string;
}

interface ContentItemFormProps {
  stepId: number;
  stepTypeId: number;
  clientId?: number;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const ContentItemForm: React.FC<ContentItemFormProps> = ({
  stepId,
  stepTypeId,
  clientId,
  onSubmit,
  onCancel
}) => {
  const [postTypes, setPostTypes] = useState<PostType[]>([]);
  const [loading, setLoading] = useState(false);

  const [contentItem, setContentItem] = useState({
    name: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    assigned_to: '',
    assigned_to_type: 'team',
    due_date: '',
    estimated_hours: 2,
    // Content-specific fields
    post_type_id: '',
    content_title: '',
    content_body: '',
    publish_date: '',
    publish_status: 'draft', // draft, scheduled, published, archived
    seo_keywords: '',
    featured_image_url: '',
    excerpt: '',
    author: '',
    categories: [] as string[],
    tags: [] as string[],
    custom_fields: {} as Record<string, any>
  });

  useEffect(() => {
    loadPostTypes();
  }, [clientId]);

  const loadPostTypes = async () => {
    try {
      const params = clientId ? { client_id: clientId } : {};
      const response = await axios.get(`${API_BASE_URL}/post-types`, { params });
      setPostTypes(response.data.postTypes || []);
    } catch (error) {
      console.error('Failed to load post types:', error);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'team_members': return <FileText className="w-4 h-4" />;
      case 'services': return <Share2 className="w-4 h-4" />;
      case 'news': return <FileText className="w-4 h-4" />;
      case 'events': return <FileText className="w-4 h-4" />;
      case 'testimonials': return <Share2 className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const config = {
        type: 'content',
        post_type_id: contentItem.post_type_id,
        content_title: contentItem.content_title,
        content_body: contentItem.content_body,
        publish_date: contentItem.publish_date,
        publish_status: contentItem.publish_status,
        seo_keywords: contentItem.seo_keywords,
        featured_image_url: contentItem.featured_image_url,
        excerpt: contentItem.excerpt,
        author: contentItem.author,
        categories: contentItem.categories,
        tags: contentItem.tags,
        custom_fields: contentItem.custom_fields
      };

      const payload = {
        step_type_id: stepTypeId,
        name: contentItem.name,
        description: contentItem.description,
        status: contentItem.status,
        priority: contentItem.priority,
        assigned_to: contentItem.assigned_to || null,
        assigned_to_type: contentItem.assigned_to_type,
        due_date: contentItem.due_date || null,
        estimated_hours: contentItem.estimated_hours,
        config: JSON.stringify(config),
        post_type_post_id: null // Will be filled when post is created
      };

      await onSubmit(payload);
    } catch (error) {
      console.error('Failed to create content item:', error);
      alert('Failed to create content item');
    } finally {
      setLoading(false);
    }
  };

  const selectedPostType = postTypes.find(pt => pt.id === parseInt(contentItem.post_type_id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create Content Item</h2>
            <p className="text-sm text-gray-500 mt-1">Create a new content item for publication</p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={contentItem.name}
                    onChange={(e) => setContentItem({ ...contentItem, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Write Blog Post about Q4 Products"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={contentItem.description}
                    onChange={(e) => setContentItem({ ...contentItem, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brief description of this content task..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Post Type *
                  </label>
                  <select
                    required
                    value={contentItem.post_type_id}
                    onChange={(e) => setContentItem({ ...contentItem, post_type_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select post type...</option>
                    {postTypes.map(pt => (
                      <option key={pt.id} value={pt.id}>
                        {pt.name} ({pt.category})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Status
                  </label>
                  <select
                    value={contentItem.publish_status}
                    onChange={(e) => setContentItem({ ...contentItem, publish_status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Content Details */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={contentItem.content_title}
                    onChange={(e) => setContentItem({ ...contentItem, content_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="The actual title that will be published"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Body
                  </label>
                  <textarea
                    value={contentItem.content_body}
                    onChange={(e) => setContentItem({ ...contentItem, content_body: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Main content text..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Excerpt / Summary
                  </label>
                  <textarea
                    value={contentItem.excerpt}
                    onChange={(e) => setContentItem({ ...contentItem, excerpt: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Brief summary for previews..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author
                    </label>
                    <input
                      type="text"
                      value={contentItem.author}
                      onChange={(e) => setContentItem({ ...contentItem, author: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Author name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Featured Image URL
                    </label>
                    <input
                      type="url"
                      value={contentItem.featured_image_url}
                      onChange={(e) => setContentItem({ ...contentItem, featured_image_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SEO Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={contentItem.seo_keywords}
                    onChange={(e) => setContentItem({ ...contentItem, seo_keywords: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>
              </div>
            </div>

            {/* Task Management */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Management</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={contentItem.status}
                    onChange={(e) => setContentItem({ ...contentItem, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="waiting">Waiting</option>
                    <option value="blocked">Blocked</option>
                    <option value="completed">Completed</option>
                    <option value="skipped">Skipped</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={contentItem.priority}
                    onChange={(e) => setContentItem({ ...contentItem, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned To Type
                  </label>
                  <select
                    value={contentItem.assigned_to_type}
                    onChange={(e) => setContentItem({ ...contentItem, assigned_to_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="team">Team Member</option>
                    <option value="client">Client</option>
                    <option value="external">External</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned To
                  </label>
                  <input
                    type="text"
                    value={contentItem.assigned_to}
                    onChange={(e) => setContentItem({ ...contentItem, assigned_to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Name or email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={contentItem.due_date}
                    onChange={(e) => setContentItem({ ...contentItem, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={contentItem.publish_date}
                    onChange={(e) => setContentItem({ ...contentItem, publish_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Hours
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={contentItem.estimated_hours}
                    onChange={(e) => setContentItem({ ...contentItem, estimated_hours: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Content Item'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentItemForm;
